// components/brand/BrandForm/ImageUpload.js - Improved with better error handling
import React, { useState, useEffect } from 'react';
import ImageUploader from '../../ImageUploader';
import { sanitizeBrandName } from '../../../utils/imageUpload';

// Maximum image dimension (width or height)
const MAX_IMAGE_DIMENSION = 1200;
// Maximum file size in bytes (500KB)
const MAX_FILE_SIZE = 500 * 1024;

function ImageUpload({ logoImage, coverImage, onImageChange, brandId = null, brandName = null }) {
  const [logoPreview, setLogoPreview] = useState(logoImage || '');
  const [coverPreview, setCoverPreview] = useState(coverImage || '');
  const [processingLogo, setProcessingLogo] = useState(false);
  const [processingCover, setProcessingCover] = useState(false);

  // Generate a consistent temp ID for new brands (for Firebase Storage folder structure)
  const [tempBrandId] = useState(() => brandId || `temp_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`);
  const effectiveBrandId = brandId || tempBrandId;
  const folderName = sanitizeBrandName(brandName, effectiveBrandId);
  
  // Update logo and cover previews when props change
  useEffect(() => {
    setLogoPreview(logoImage || '');
    setCoverPreview(coverImage || '');
  }, [logoImage, coverImage]);
  
  const handleLogoSelect = async (finalUrl) => {
    console.log("Logo selected:", finalUrl ? "Firebase Storage URL" : "No");

    if (!finalUrl) {
      setLogoPreview('');
      onImageChange('logoImage', '');
      return;
    }

    // finalUrl is now either a Firebase Storage URL or base64 (for compatibility)
    setLogoPreview(finalUrl);
    onImageChange('logoImage', finalUrl);
    console.log("Logo uploaded successfully:", finalUrl);
  };
  
  const handleCoverSelect = async (finalUrl) => {
    console.log("Cover selected:", finalUrl ? "Firebase Storage URL" : "No");

    if (!finalUrl) {
      setCoverPreview('');
      onImageChange('coverImage', '');
      return;
    }

    // finalUrl is now either a Firebase Storage URL or base64 (for compatibility)
    setCoverPreview(finalUrl);
    onImageChange('coverImage', finalUrl);
    console.log("Cover uploaded successfully:", finalUrl);
  };
  
  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center">
        <h4 className="text-lg font-medium text-gray-700">Images</h4>
        <div className="relative ml-2 group">
          <svg className="h-5 w-5 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="absolute left-0 bottom-6 w-80 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
            <div className="font-medium mb-2">Image Upload Tips:</div>
            <ul className="space-y-1 text-gray-200">
              <li>• Supported formats: JPG, PNG</li>
              <li>• Recommended size: Under 500KB for best results</li>
              <li>• Images will be automatically compressed to fit 500KB limit</li>
              <li>• Use high-quality images for better compression results</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="relative">
          {processingLogo && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-gray-600">Processing logo...</p>
              </div>
            </div>
          )}
          <ImageUploader
            label="Logo Image"
            id="logo-upload"
            initialImageUrl={logoPreview}
            onImageSelect={handleLogoSelect}
            maxSizeMB={0.5}
            previewHeight="max-h-40"
            accept="image/jpeg, image/png, image/jpg"
            disabled={processingLogo}
            storageConfig={{
              folderPath: `brands/${folderName}/logo`
            }}
          />
        </div>
        
        <div className="relative">
          {processingCover && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-gray-600">Processing cover...</p>
              </div>
            </div>
          )}
          <ImageUploader
            label="Cover Image"
            id="cover-upload"
            initialImageUrl={coverPreview}
            onImageSelect={handleCoverSelect}
            maxSizeMB={0.5}
            previewHeight="max-h-40"
            accept="image/jpeg, image/png, image/jpg"
            disabled={processingCover}
            storageConfig={{
              folderPath: `brands/${folderName}/cover`
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default ImageUpload;